/* globals ENVIRONMENT */

export default class Utilities {
  /**
   * Adds a listener via standard method or via attach event when browser is < IE8
   *
   * @param {String} eventName - Name of the event to bound the lister to
   * @param {Function} listener - The listener function
   */
  static addListener(eventName, listener) {
    if (typeof window.addEventListener === 'function') {
      window.addEventListener(eventName, listener);
    } else {
      window.attachEvent(`on${eventName}`, listener);
    }
  }

  /**
   * Checks the provided user agent against a regex
   *
   * @param {Sensor} sensor - Sensor instance
   * @param {String} useragent - User agent string
   * @return {Boolean} Check result
   */
  static checkUserAgentBlacklist(sensor, useragent) {
    const { systemConfig, logger } = sensor;
    if (systemConfig.check.userAgentBlacklist === false) {
      return true;
    }
    const regex = new RegExp(systemConfig.userAgentBlacklist);
    if (regex.test(useragent.toLocaleLowerCase()) === true) {
      logger.debug('user agent NOT valid (found in blacklist)');
      return false;
    }
    logger.debug('user agent valid (not found in blacklist)');
    return true;
  }

  /**
   * Checks if the given user agent is on the user agent black list
   *
   * @param {Sensor} sensor - Current sensor instance
   * @param {String} useragent - User agent string
   */
  static checkUserAgentWhitelist(sensor, useragent) {
    const { systemConfig, logger } = sensor;
    if (systemConfig.check.userAgentWhitelist === false) {
      return true;
    }
    const regex = new RegExp(systemConfig.userAgentWhitelist);
    if (regex.test(useragent.toLocaleLowerCase()) === false) {
      logger.debug('user agent NOT valid (not found in whitelist)');
      return false;
    }
    logger.debug('user agent valid (found in whitelist)');
    return true;
  }

  /**
   * Checks the location part against the provided regex parts
   *
   * @param {Array.<String>} parts - Regexp parts
   * @param {String} locationPart - Location part as string
   * @returns {Boolean} Check result
   */
  static checkValidLocationParts(parts, locationPart) {
    for (let i = 0, iLen = parts.length; i < iLen; i += 1) {
      const part = parts[i];
      const regex = new RegExp(part);
      const match = regex.test(locationPart);
      if (match) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks the provided url against some regex pattern included in the provided system configuration
   *
   * @param {Sensor} sensor - Sensor instance
   * @param {Object} location - Current window.location object
   * @returns {Boolean} Check result
   */
  static checkValidLocation(sensor, location) {
    const { systemConfig, logger } = sensor;
    if (systemConfig.check.urlWhitelist === false) {
      return true;
    }
    if (!location) {
      logger.debug('invalid location provided');
      return false;
    }
    let locationOrigin;
    if (!location.origin) {
      const { protocol, hostname, port } = location;
      locationOrigin = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    } else {
      locationOrigin = location.origin;
    }
    const matches = [];
    for (let i = 0, iLen = systemConfig.urlWhitelist.length; i < iLen; i += 1) {
      const whitelist = systemConfig.urlWhitelist[i];
      const match = {
        origin: true,
        paths: true,
        queries: true,
      };
      if (whitelist.paths) {
        match.paths = Utilities.checkValidLocationParts(whitelist.paths, location.pathname);
      }
      if (whitelist.queries) {
        match.queries = Utilities.checkValidLocationParts(whitelist.queries, location.search);
      }
      const regex = new RegExp(whitelist.origin);
      match.origin = regex.test(locationOrigin);
      matches.push(match);
    }
    const pos = matches.findIndex(match => match.origin && match.paths && match.queries);
    if (pos > -1) {
      logger.debug('location valid (found in whitelist)');
      return true;
    }
    logger.debug('location NOT valid (not found in whitelist)');
    return false;
  }

  /**
   * Cross browser compatible url parser. It will create an link and sets the href attribute. Then
   * you have access to the URL parts
   *
   * @param {String} url - Url to parse
   * @returns {Object} Parsed url
   */
  static parseUrl(url) {
    const parser = document.createElement('a');
    parser.href = url;
    return {
      protocol: parser.protocol,
      hostname: parser.hostname,
      post: parser.port,
      pathname: parser.pathname,
      search: parser.search,
      hash: parser.hash,
      host: parser.host,
      origin: `${parser.protocol}//${parser.hostname}${parser.port ? `:${parser.port}` : ''}`,
    };
  }

  /**
   * Checks for a valid environment by checking the provided useragent and url against some regex patterns included in the provided system
   * configuration
   *
   * @param {Sensor} sensor - Sensor instance
   * @param {String} useragent - User agent string
   * @param {String} location - The current location object
   * @return {Boolean} Check result
   */
  static checkValidEnvironments(sensor, useragent, location) {
    if (sensor.state.hybrid.detected === false) {
      return Utilities.checkUserAgentBlacklist(sensor, useragent)
        && Utilities.checkUserAgentWhitelist(sensor, useragent)
        && Utilities.checkValidLocation(sensor, location);
    }
    return true;
  }

  /**
   * Compresses a given text with LZW algorithm
   *
   * @param {String} text - Text to compress
   * @return {string} Compressed text
   */
  static compress(text) {
    const dict = new Map();
    const data = text.split('');
    const out = [];
    let currChar;
    let phrase = data[0];
    let code = 256;
    for (let i = 1, iLen = data.length; i < iLen; i += 1) {
      currChar = data[i];
      if (dict.has(phrase + currChar)) {
        phrase += currChar;
      } else {
        out.push(phrase.length > 1 ? dict.get(phrase) : phrase.codePointAt(0));
        dict.set(phrase + currChar, code);
        code += 1;
        phrase = currChar;
      }
    }
    out.push(phrase.length > 1 ? dict.get(phrase) : phrase.codePointAt(0));
    for (let i = 0, iLen = out.length; i < iLen; i += 1) {
      out[i] = String.fromCodePoint(out[i]);
    }
    return out.join('');
  }

  /**
   * Creates the data structure for the profiling information
   *
   * @return {Object} Default empty profile
   */
  static createDefaultProfile() {
    return {
      site: {
        pi: 0,
        visits: 0,
        viewtime: 0,
        hourlyClients: 0,
        dailyClients: 0,
        monthlyClients: 0,
        hourlyWnk: 0,
        loyalty: {},
      },
      category: {},
    };
  }

  /**
   * Takes a LZW compressed text and decompress it
   *
   * @param {String} text - Compressed text
   * @return {String} - Decompressed text
   */
  static decompress(text) {
    const dict = new Map(); // Use a Map!
    const data = Array.from(text);
    let currChar = data[0];
    let oldPhrase = currChar;
    const out = [currChar];
    let code = 256;
    let phrase;
    for (let i = 1, iLen = data.length; i < iLen; i += 1) {
      const currCode = data[i].codePointAt(0);
      if (currCode < 256) {
        phrase = data[i];
      } else {
        phrase = dict.has(currCode) ? dict.get(currCode) : (oldPhrase + currChar);
      }
      out.push(phrase);
      const cp = phrase.codePointAt(0);
      currChar = String.fromCodePoint(cp);
      dict.set(code, oldPhrase + currChar);
      code += 1;
      oldPhrase = phrase;
    }
    return out.join('');
  }

  /**
   * Checks the LSO functionality aggressively by adding a temporary value to the LSO and and remove it directly after that. If something in the try
   * and catch clause fails we will return false.
   *
   * @return {boolean}
   */
  static lsoSupport() {
    const mod = 'acsam_lso_check';
    try {
      window.localStorage.setItem(mod, mod);
      window.localStorage.removeItem(mod);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Checks if local storage features are present and functional in current browser and if sensor should use it. When sensor should use LSO we check
   * the functionality aggressively by adding a temporary value to the LSO and and remove it directly after that. If something in the try and catch
   * clause fails we will return false.
   *
   * @param {Sensor} sensor - Sensor instance
   * @return {Boolean} Check result
   */
  static checkLso(sensor) {
    if (sensor.state.persistence === true) {
      return Utilities.lsoSupport();
    }
    return false;
  }

  /**
   * Check if JSON is supported by current browser
   *
   * @return {Boolean} Check result
   */
  static checkJSON() {
    return typeof JSON === 'object' && typeof JSON.parse === 'function' && typeof JSON.stringify === 'function';
  }

  /**
   * Checks if the browser supports the post message interface for communication between different window instances
   *
   * @return {Boolean} Check result
   */
  static checkPostMessage() {
    return typeof window.postMessage === 'function';
  }

  /**
   * Creates a script tag and resolves when script was successfully loaded or rejects when script was failed to load
   *
   * @param url - The url of the script to load
   * @param [integrity] - The optional integrity attribute to checkup the integrity of the script
   * @param [crossOrigin=anonymous] - The optional cross origin attribute
   * @return {Promise} Resolves when script successfully loads or rejects when an error occurred
   */
  static createScriptTag(url, integrity, crossOrigin = 'anonymous') {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.setAttribute('type', 'text/javascript');
      script.setAttribute('src', url);
      if (integrity) {
        script.setAttribute('integrity', integrity);
      }
      script.setAttribute('crossorigin', crossOrigin);
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  /**
   * Computes the day number since 0 January 0 CE (Gregorian)
   *
   * @param {Number} year - ISO 8601 year
   * @param {Number} month - ISO 8601 month
   * @param {Number} day -  Day of the ISO 8601 month
   */
  static getDayNumber(year, month, day) {
    let y = year;
    let m = month;
    // Take a leap year into account
    if (month < 3) {
      y -= 1;
      m += 12;
    }
    return Math.floor(365.25 * y)
      - Math.floor(y / 100)
      + Math.floor(y / 400)
      + Math.floor(30.6 * (m + 1))
      + day - 62;
  }

  /**
   * Gets the week number of a given date
   *
   * @param {Date} date - The date for determining the week number
   * @return {String} Year and week number as notation YYYYWW
   */
  static getWeekNumber(date) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const wDay = date.getDay();
    // Weekdays will be numbered 1 to 7
    const weekDay = ((wDay + 6) % 7) + 1;
    let isoYear = year;
    let day0 = Utilities.getDayNumber(year, 1, 0);
    let weekDay0 = ((day0 + 4) % 7) + 1;
    const dayOfYear = Utilities.getDayNumber(year, month + 1, day);
    let weekNumber = Math.floor((dayOfYear - day0 + weekDay0 + 6) / 7) - Math.floor((weekDay0 + 3) / 7);
    // Check whether the last few days of December belong to the next year's ISO week
    if (month === 11 && (day - weekDay) > 27) {
      weekNumber = 1;
      isoYear += 1;
    }
    // Check whether the first few days of January belong to the previous year's ISO week
    if (month === 0 && (weekDay - day) > 3) {
      day0 = Utilities.getDayNumber(year - 1, 1, 0);
      weekDay0 = ((day0 + 4) % 7) + 1;
      weekNumber = Math.floor((dayOfYear - day0 + weekDay0 + 6) / 7) - Math.floor((weekDay0 + 3) / 7);
      isoYear -= 1;
    }
    if (weekNumber < 10) {
      return `${isoYear}0${weekNumber}`;
    }
    return `${isoYear}${weekNumber}`;
  }

  /**
   * @typedef {Object} DismantledDate
   * @property {String} year
   * @property {String} month
   * @property {String} day
   * @property {String} weekNumber
   * @property {String} hour
   */
  /**
   * Dismantles a given date to concatenated date parts beginning with year and ending with hour.
   *
   * @param {Date} date - The date value to dismantle
   * @return {DismantledDate} - A collection of concatenated date parts
   */
  static dismantleDate(date) {
    // First replace all none numeric values with nothing
    const dateText = date.toJSON().replace(/[^0-9]/g, '');
    // Extract date concatenated date parts beginning with year
    return {
      year: dateText.slice(0, 4),
      month: dateText.slice(0, 6),
      weekNumber: `${this.getWeekNumber(date)}`,
      day: dateText.slice(0, 8),
      hour: dateText.slice(0, 10),
    };
  }

  /**
   * Gets transmission protocol to be used to communicate with relay ('https' | 'http')
   *
   * @return {String} 'https:' | 'http:'
   */
  static getTransmissionProtocol() {
    return ['development', 'testing'].includes(ENVIRONMENT) ? 'http:' : 'https:';
  }

  /**
   * Gets the host part of the current document referrer or an empty string when url segments are less than 3
   *
   * @param {String} url - URL to extract the hostname
   * @return {string} - Host part of the document referrer
   */
  static getUrlHost(url) {
    if (url) {
      const location = Utilities.getLocation(url);
      if (location) {
        return location.hostname;
      }
    }
    return '';
  }

  /**
   * Gets the current screen size and color depth of current window instance
   *
   * @return {String} - Concatenated string with height, width and color depth of current window instanc
   */
  static getScreen() {
    const { width, height, colorDepth } = window.screen;
    return `${width}x${height}x${colorDepth}`;
  }

  /**
   * Checks if the 'Do Not Track' is enabled in current browser. Before running the check it will check id DNT should be respected by the sensor
   *
   * @param {Sensor} sensor - current sensor instance
   * @return {Boolean} Check result
   */
  static isDoNotTrack(sensor) {
    if (sensor.state.nt) {
      // eslint-disable-next-line eqeqeq
      if (window.navigator.msDoNotTrack && window.navigator.msDoNotTrack == '1') {
        return true;
      }
      // eslint-disable-next-line eqeqeq
      if (window.navigator.doNotTrack && (window.navigator.doNotTrack == 'yes' || window.navigator.doNotTrack == '1')) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if the current browser is of type Internet Explorer
   *
   * @param {Number} version - Version to test
   * @param {String} comparison - Activates comparison
   * @return {Boolean} check result
   */
  static isIE(version, comparison) {
    let cc = 'IE';
    const b = document.createElement('B');
    if (version) {
      cc += ` ${version}`;
      if (comparison) {
        cc = `${comparison} ${cc}`;
      }
    }
    b.innerHTML = `<!--[if ${cc}]><b id="iecctest"></b><![endif]-->`;
    document.documentElement.appendChild(b);
    const result = !!document.getElementById('iecctest');
    document.documentElement.removeChild(b);
    return result;
  }

  /**
   *
   * @param value
   * @return {boolean}
   */
  static isJSON(value) {
    try {
      JSON.parse(value);
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Checks if a given value is plain object
   *
   * @param {*} value - The value to check
   * @return {Boolean} Check result
   */
  static isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  /**
   * Shortens the keys of a provided data object to it's shorter counterparts included in a provided mapping
   *
   * @param {Object} data - Data which keys should be transformed to shorter one
   * @param {Object} mapping - Mapping with the abbreviation version of key
   */
  static transformKeys(data, mapping) {
    if (Utilities.isPlainObject(data) === false) {
      return data;
    }
    const result = {};
    const dataKeys = Object.keys(data);
    const mappingKeys = Object.keys(mapping);
    for (let i = 0, iLen = dataKeys.length; i < iLen; i += 1) {
      const dataKey = dataKeys[i];
      const dataValue = data[dataKey];
      const resultKey = mappingKeys.indexOf(dataKey) > -1 ? mapping[dataKey] : dataKey;
      if (Utilities.isPlainObject(dataValue) === true) {
        result[resultKey] = Utilities.transformKeys({ ...dataValue }, mapping);
      } else if (Array.isArray(dataValue) === true) {
        result[resultKey] = [...dataValue];
        // Iterate through each item in array and abbreviate the keys
        for (let j = 0, jLen = dataValue.length; j < jLen; j += 1) {
          // Overwrite object specific keys in array collections
          result[resultKey][j] = Utilities.transformKeys(dataValue[j], mapping);
        }
      } else {
        result[resultKey] = dataValue;
      }
    }
    return result;
  }

  /**
   * Creates a date string in format YYYYMMDD
   *
   * @return {String} Date string in format YYYYMMDD
   */
  static today() {
    const date = new Date();
    return date.toJSON().replace(/[^0-9]/g, '').slice(0, 8);
  }

  /**
   * Serializes the given parameters (key value pairs) to a ur encoded query string
   *
   * @param {Object} parameters
   * @return {string} Url encoded query string
   */
  static serializeQueryParameters(parameters) {
    const query = [];
    const keys = Object.keys(parameters);
    for (let i = 0, iLen = keys.length; i < iLen; i += 1) {
      const key = keys[i];
      const value = parameters[key];
      query.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
    return query.join('&');
  }

  /**
   * Flips key value pairs of a given object
   *
   * @param {Object} obj - Object to flip key values
   * @return {Object} Object with flipped key values
   */
  static flipKeyValues(obj) {
    return Object.keys(obj).reduce((ret, key) => {
      // eslint-disable-next-line no-param-reassign
      ret[obj[key]] = key;
      return ret;
    }, {});
  }

  /**
   *
   * @param source
   */
  static deepClone(source) {
    // If the source isn't an Object or Array, throw an error.
    if (!(source instanceof Object) || source instanceof Date || source instanceof String) {
      throw new Error('Only objects or arrays are supported.');
    }
    const target = source instanceof Array ? [] : {};
    // eslint-disable-next-line no-restricted-syntax
    for (const prop in source) {
      // If the current property is an Array or Object, recursively clone it, else copy it's value
      if (source[prop] instanceof Object && !(source[prop] instanceof Date) && !(source[prop] instanceof String)) {
        target[prop] = Utilities.deepClone(source[prop]);
      } else {
        target[prop] = source[prop];
      }
    }
    return target;
  }

  /**
   * Determines the days between two unix timestamps
   * @param {Number} start - Start date
   * @param {Number} end - End Date
   * @return {Number} Number of days between start and end date
   */
  static diffDays(start, end) {
    const timeDiff = Math.abs(end - start);
    return Math.floor(timeDiff / (1000 * 3600 * 24));
  }

  /**
   *
   * @param {Object} options
   */
  static checkIsWebView(options) {
    if (window.webkit && window.webkit.messageHandlers && options.ios in window.webkit.messageHandlers
      && typeof window.webkit.messageHandlers[options.ios].postMessage === 'function') {
      return 'ios';
    }
    if (options.android in window) {
      return 'android';
    }
    return '';
  }

  /**
   * Determines if current window.location and DomainServiceName are on the same Second-Level-Domain and TLD
   *
   * @param {Sensor} sensor - Web sensor instance
   * @param {String} serviceDomainName given serviceDomainName in sensor initParameters
   * @param {String} hostname 'window.location.hostname' context hostname without port
   * @return { Boolean } true if in first party context
   */
  static checkIsFirstPartyContext(sensor, serviceDomainName, hostname) {
    // Bypass first party checkup when web sensor is part of an IOMa webview
    if (sensor.state.hybrid.detected === false) {
      let host;
      // Check if host name is an href (e.g. AMP canonical url)
      if (hostname.includes('http')) {
        // Extract hostname from dismantling the provided hostname
        const hostPart = Utilities.getLocation(hostname).hostname;
        // Return false when no match
        if (!hostPart) {
          return false;
        }
        host = hostPart.split('.').reverse();
      } else {
        // Normal hostname processing
        host = hostname.split('.').reverse();
      }
      const sdn = serviceDomainName.split('.').reverse();
      sdn.pop();
      for (let index = 0; index < sdn.length; index += 1) {
        if (sdn[index] !== host[index]) {
          return false;
        }
      }
      return true;
    }
    return true;
  }

  /**
   * Dismantles an absolute href to its url parts. Works cross-browser compatible an needs no DOM
   *
   * @param {String} href - Href to dismantle
   * @return {Object|Null} URL parts or null when regex match failed
   */
  static getLocation(href) {
    // eslint-disable-next-line no-useless-escape
    const match = href.match(/^(https?:)\/\/(([^:\/?#]*)(?::([0-9]+))?)([\/]?[^?#]*)(\?[^#]*|)(#.*|)$/);
    if (match) {
      return {
        href,
        protocol: match[1],
        host: match[2],
        hostname: match[3],
        port: match[4],
        pathname: match[5],
        search: match[6],
        hash: match[7],
      };
    }
    return null;
  }
}
